import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ValidatedPdbResidue } from '../types';
import {
  residueDisplayLabel,
  residuesForPdb,
  toMainModelSelection,
  toViewerSelection,
} from '../utils/validatedResidues';

// Declare 3Dmol global type
declare global {
  interface Window {
    $3Dmol: any;
  }
}

interface ProteinViewerProps {
  pdbId: string;
  validatedResidues?: ValidatedPdbResidue[];
  mappingStatus?: 'verified' | 'quarantined' | 'missing' | 'structure_unavailable';
  mappingValidationReason?: string;
  bindingPartners?: string | string[]; // New: binding partner information
  entryId?: number; // Add entry ID for fetching binding partner structures
  onClose?: () => void;
  onExpand?: () => void; // New: expand to fullscreen functionality
  isFullscreen?: boolean;
  availablePdbIds?: string[];  // All available PDB IDs for this protein
  representativePdbId?: string; // The representative structure
  onPdbChange?: (newPdbId: string) => void;
}

interface PDBInfo {
  success: boolean;
  pdb_id: string;
  title?: string;
  resolution?: number[];
  experimental_method?: string;
  release_date?: string;
  organism?: any[];
  message?: string;
}

interface BindingPartnerStructure {
  name: string;
  type: string;
  description: string;
  color: string;
  structure_available: boolean;
  pdb_candidates: string[];
  recommended_pdb?: string;
}

const InformationCircleIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
  </svg>
);

const XMarkIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ExpandIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2m8-16h2a2 2 0 012 2v2m-4 12h2a2 2 0 002-2v-2" />
  </svg>
);

const ChevronDownIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

const StarIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const RefreshIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const ProteinViewer: React.FC<ProteinViewerProps> = ({ 
  pdbId, 
  validatedResidues,
  mappingStatus,
  mappingValidationReason,
  bindingPartners,
  entryId,
  onClose,
  onExpand,
  isFullscreen = false,
  availablePdbIds = [],
  representativePdbId,
  onPdbChange
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const [selectedPdbId, setSelectedPdbId] = useState(pdbId);
  const [showPdbSelector, setShowPdbSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdbInfo, setPdbInfo] = useState<PDBInfo | null>(null);
  const [viewer, setViewer] = useState<any>(null);
  const [representationStyle, setRepresentationStyle] = useState('cartoon');
  const [colorScheme, setColorScheme] = useState('chain');
  const [showLabels, setShowLabels] = useState(false);
  const [availableChains, setAvailableChains] = useState<string[]>([]);
  const [chainInfo, setChainInfo] = useState<Map<string, any>>(new Map());
  const [visibleChains, setVisibleChains] = useState<Set<string>>(new Set());
  const visibleChainsRef = useRef<Set<string>>(new Set());
  const loadGenerationRef = useRef(0);
  const activeLoadControllerRef = useRef<AbortController | null>(null);
  const [showChainSelector, setShowChainSelector] = useState(false);
  const [bindingPartnersList, setBindingPartnersList] = useState<any[]>([]);
  const [selectedBindingPartner, setSelectedBindingPartner] = useState<string | null>(null);
  const [showBindingPartnerPanel, setShowBindingPartnerPanel] = useState(false);
  const [partnerStructures, setPartnerStructures] = useState<BindingPartnerStructure[]>([]);
  const [loadedPartnerModels, setLoadedPartnerModels] = useState<Map<string, any>>(new Map());
  const [showPartnerStructures, setShowPartnerStructures] = useState(false);
  
  const currentPdbId = selectedPdbId || pdbId;
  const currentValidatedResidues = useMemo(
    () => mappingStatus === 'verified'
      ? residuesForPdb(validatedResidues, currentPdbId)
      : [],
    [validatedResidues, currentPdbId, mappingStatus]
  );

  // Load 3Dmol.js script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://3dmol.csb.pitt.edu/build/3Dmol-min.js';
    script.async = true;
    script.onload = () => {
    };
    script.onerror = () => {
      setError('Failed to load 3Dmol.js library');
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  useEffect(() => () => {
    loadGenerationRef.current += 1;
    activeLoadControllerRef.current?.abort();
  }, []);

  // Helper function to get professional chain colors
  const getChainColor = useCallback((chainId: string) => {
    const colorPalette = {
      'A': '#3B82F6', // Blue
      'B': '#EF4444', // Red  
      'C': '#10B981', // Green
      'D': '#F59E0B', // Yellow
      'E': '#8B5CF6', // Purple
      'F': '#F97316', // Orange
      'G': '#06B6D4', // Cyan
      'H': '#EC4899', // Pink
      'I': '#84CC16', // Lime
      'J': '#6B7280'  // Gray
    };
    return colorPalette[chainId as keyof typeof colorPalette] || '#6B7280';
  }, []);

  // Helper function to create a label for a residue
  const createLabelForResidue = useCallback((atoms: any[], resNum: number, chainId: string) => {
    if (!atoms || atoms.length === 0) return null;
    
    let labelX = 0, labelY = 0, labelZ = 0;
    let residueName = '';
    let caAtom: any = null;

    // Look for alpha carbon (CA) atom first
    atoms.forEach((atom: any) => {
      if (!residueName && atom.resn) {
        residueName = atom.resn;
      }
      if (atom.atom === 'CA') {
        caAtom = atom;
      }
    });

    if (caAtom) {
      // Use CA atom position with small offset for better visibility
      labelX = caAtom.x;
      labelY = caAtom.y;
      labelZ = caAtom.z + 3.0; // Offset above the residue
    } else {
      // Fallback to center of mass with offset
      atoms.forEach((atom: any) => {
        labelX += atom.x;
        labelY += atom.y;
        labelZ += atom.z;
      });
      labelX /= atoms.length;
      labelY /= atoms.length;
      labelZ = (labelZ / atoms.length) + 3.0; // Offset above
    }

    // Convert 3-letter amino acid code to 1-letter code
    const aminoAcidMap: { [key: string]: string } = {
      'ALA': 'A', 'ARG': 'R', 'ASN': 'N', 'ASP': 'D', 'CYS': 'C',
      'GLU': 'E', 'GLN': 'Q', 'GLY': 'G', 'HIS': 'H', 'ILE': 'I',
      'LEU': 'L', 'LYS': 'K', 'MET': 'M', 'PHE': 'F', 'PRO': 'P',
      'SER': 'S', 'THR': 'T', 'TRP': 'W', 'TYR': 'Y', 'VAL': 'V'
    };
    
    const singleLetterCode = aminoAcidMap[residueName] || residueName.charAt(0);
    const labelText = chainId ? `${singleLetterCode}${resNum}:${chainId}` : `${singleLetterCode}${resNum}`;

    return {
      text: labelText,
      options: {
        position: { x: labelX, y: labelY, z: labelZ },
        backgroundColor: '#dc2626',
        backgroundOpacity: 0.95,
        fontColor: 'white',
        fontSize: 14,
        fontOpacity: 1.0,
        borderThickness: 2,
        borderColor: '#991b1b',
        showBackground: true,
        alignment: 'center',
        inFront: true,
        fixed: false
      }
    };
  }, []);

  // Helper function to parse and classify binding partners
  const parseBindingPartners = useCallback((partners?: string | string[]) => {
    if (!partners) return [];
    
    let partnerArray: string[] = [];
    if (typeof partners === 'string') {
      // Split by common delimiters
      partnerArray = partners.split(/[,;|]/).map(p => p.trim()).filter(p => p.length > 0);
    } else {
      partnerArray = partners;
    }
    
    return partnerArray.map((partner, index) => {
      const partnerLower = partner.toLowerCase();
      
      // Classify partner type based on common patterns
      let type = 'Unknown';
      let description = '';
      let color = '#6B7280';
      
      // Small molecules and drugs
      if (partnerLower.includes('atp') || partnerLower.includes('adp') || partnerLower.includes('gtp') || 
          partnerLower.includes('nad') || partnerLower.includes('fad')) {
        type = 'Cofactor';
        description = 'Enzymatic cofactor';
        color = '#F59E0B';
      } else if (partnerLower.includes('inhibitor') || partnerLower.includes('drug') || 
                 partnerLower.includes('compound') || /^[A-Z]{2,4}[-\d]+$/.test(partner)) {
        type = 'Small Molecule';
        description = 'Drug or inhibitor';
        color = '#EF4444';
      } else if (partnerLower.includes('peptide') || /peptide/.test(partnerLower)) {
        type = 'Peptide';
        description = 'Short protein sequence';
        color = '#8B5CF6';
      } else if (partnerLower.includes('protein') || /\b[A-Z][a-z]+\s*\d*\b/.test(partner) || 
                 partner.length > 20) {
        type = 'Protein';
        description = 'Protein binding partner';
        color = '#10B981';
      } else if (partnerLower.includes('dna') || partnerLower.includes('rna') || 
                 partnerLower.includes('nucleic')) {
        type = 'Nucleic Acid';
        description = 'DNA/RNA binding partner';
        color = '#06B6D4';
      } else if (partnerLower.includes('ion') || /\b(ca2\+|mg2\+|zn2\+|fe2\+|mn2\+|k\+|na\+)\b/.test(partnerLower)) {
        type = 'Ion';
        description = 'Metal ion or cofactor';
        color = '#F97316';
      }
      
      return {
        id: `partner_${index}`,
        name: partner,
        type,
        description,
        color,
        originalIndex: index
      };
    });
  }, []);

  // Helper function to analyze chain composition and type
  const analyzeChainType = (atoms: any[]) => {
    let proteinCount = 0;
    let nucleicCount = 0;
    let ligandCount = 0;
    let waterCount = 0;
    let ionCount = 0;
    
    const residueTypes = new Set();
    
    atoms.forEach((atom: any) => {
      if (atom.resn) {
        residueTypes.add(atom.resn);
        
        // Standard amino acids
        if (['ALA', 'ARG', 'ASN', 'ASP', 'CYS', 'GLU', 'GLN', 'GLY', 'HIS', 'ILE', 
             'LEU', 'LYS', 'MET', 'PHE', 'PRO', 'SER', 'THR', 'TRP', 'TYR', 'VAL'].includes(atom.resn)) {
          proteinCount++;
        }
        // Nucleic acids
        else if (['A', 'T', 'G', 'C', 'U', 'DA', 'DT', 'DG', 'DC'].includes(atom.resn)) {
          nucleicCount++;
        }
        // Water
        else if (['HOH', 'WAT', 'H2O'].includes(atom.resn)) {
          waterCount++;
        }
        // Common ions
        else if (['NA', 'CL', 'K', 'CA', 'MG', 'ZN', 'FE', 'MN'].includes(atom.resn)) {
          ionCount++;
        }
        // Everything else is likely a ligand
        else {
          ligandCount++;
        }
      }
    });
    
    // Determine primary type
    let type = 'Unknown';
    let description = '';
    
    if (proteinCount > 0 && proteinCount > nucleicCount && proteinCount > ligandCount) {
      type = 'Protein';
      description = `${proteinCount} amino acids`;
    } else if (nucleicCount > 0 && nucleicCount > proteinCount) {
      type = 'Nucleic Acid';
      description = `${nucleicCount} nucleotides`;
    } else if (ligandCount > 0 && ligandCount > proteinCount && ligandCount > nucleicCount) {
      type = 'Ligand';
      description = `${ligandCount} atoms`;
    } else if (waterCount > 0 && waterCount > proteinCount) {
      type = 'Water';
      description = `${waterCount} molecules`;
    } else if (ionCount > 0) {
      type = 'Ions';
      description = `${ionCount} ions`;
    }
    
    return {
      type,
      description,
      atomCount: atoms.length,
      residueCount: residueTypes.size,
      residueTypes: Array.from(residueTypes).sort()
    };
  };

  // Helper function to detect and analyze available chains
  const detectAvailableChains = useCallback((viewerInstance: any) => {
    if (!viewerInstance) return;
    
    try {
      const model = viewerInstance.getModel();
      if (!model) return;
      
      // Get all atoms and group by chain
      const allAtoms = model.selectedAtoms({});
      const chainAtoms = new Map<string, any[]>();
      
      allAtoms.forEach((atom: any) => {
        if (atom.chain && atom.chain.trim() !== '') {
          const chainId = atom.chain.trim();
          if (!chainAtoms.has(chainId)) {
            chainAtoms.set(chainId, []);
          }
          chainAtoms.get(chainId)!.push(atom);
        }
      });
      
      // Analyze each chain
      const chains = Array.from(chainAtoms.keys()).sort();
      const newChainInfo = new Map();
      
      chains.forEach(chainId => {
        const atoms = chainAtoms.get(chainId)!;
        const analysis = analyzeChainType(atoms);
        
        // Count only validated coordinates assigned to this chain.
        const exositeCount = currentValidatedResidues.filter(
          residue => residue.chain_id === chainId
        ).length;
        
        newChainInfo.set(chainId, {
          ...analysis,
          exositeCount,
          color: getChainColor(chainId)
        });
      });
      
      setAvailableChains(chains);
      setChainInfo(newChainInfo);
      // Set all chains as visible by default
      const initialVisibleChains = new Set(chains);
      visibleChainsRef.current = initialVisibleChains;
      setVisibleChains(initialVisibleChains);
      
      return chains;
    } catch (e) {
      console.warn('Could not detect chains:', e);
      return [];
    }
  }, [currentValidatedResidues, getChainColor]);

  // Helper function for other parts of the code that need to apply chain-aware styles  
  // (This is now mostly used by binding partner interactions, not main chain visibility)
  const applyChainAwareStyles = useCallback((viewerInstance: any, visible: Set<string>) => {
    if (!viewerInstance) return;
    
    
    // For compatibility, just apply styles to visible chains
    const allChains = availableChains.length > 0 ? availableChains : [];
    
    allChains.forEach(chainId => {
      if (visible.has(chainId)) {
        viewerInstance.setStyle({ model: 0, chain: chainId }, {
          [representationStyle]: {
            colorscheme: colorScheme
          }
        });
      }
    });
  }, [representationStyle, colorScheme, availableChains]);

  // Helper function to apply chain visibility - COMPLETE REBUILD APPROACH
  const applyChainVisibility = useCallback((
    viewerInstance: any,
    chains: string[],
    visible: Set<string>,
    labelsEnabled: boolean = showLabels
  ) => {
    if (!viewerInstance || !chains.length) return;
    
    
    try {
      // STEP 1: NUCLEAR RESET - Clear everything completely
      viewerInstance.removeAllLabels();
      viewerInstance.setStyle({}, {});  // Remove all existing styles completely
      
      // STEP 2: Handle empty case
      if (visible.size === 0) {
        viewerInstance.render();
        return;
      }
      
      // STEP 3: Rebuild EVERYTHING from scratch - chains first
      
      chains.forEach(chainId => {
        if (visible.has(chainId)) {
          // Show this chain with full styling
          viewerInstance.setStyle({ model: 0, chain: chainId }, {
            [representationStyle]: {
              colorscheme: colorScheme
            }
          });
        }
        // Note: We don't style hidden chains at all - they remain unstyled/invisible
      });
      
      // STEP 4: Add only coordinates verified for this exact PDB and chain.
      const visibleResidues = currentValidatedResidues.filter(
        residue => visible.has(residue.chain_id)
      );

      if (visibleResidues.length > 0) {
        visibleResidues.forEach(residue => {
          try {
            viewerInstance.addStyle(toMainModelSelection(residue), {
              stick: {
                color: '#dc2626',
                radius: 0.4
              }
            });
          } catch (e) {
            // A validated coordinate can still be absent in a transiently loaded model.
          }
        });
        
        // STEP 5: Add labels for visible chains if enabled
        if (labelsEnabled) {
          
          // Get the main model (first model loaded)
          const mainModel = viewerInstance.getModel(0);
          if (mainModel) {
            visibleResidues.forEach(residue => {
              try {
                const chainAtoms = mainModel.selectedAtoms(toViewerSelection(residue));
                if (chainAtoms && chainAtoms.length > 0) {
                  const label = createLabelForResidue(
                    chainAtoms,
                    residue.residue_number,
                    residue.chain_id
                  );
                  if (label) {
                    viewerInstance.addLabel(
                      `${residueDisplayLabel(residue)}:${residue.chain_id}`,
                      label.options
                    );
                  }
                }
              } catch (e) {
              }
            });
          } else {
            console.warn('⚠️ No main model found for label creation');
          }
        }
      }
      
      // STEP 6: Final render
      viewerInstance.render();
      
    } catch (e) {
      console.error('❌ Error in complete rebuild:', e);
      viewerInstance.render(); // Try to render anyway
    }
  }, [representationStyle, colorScheme, currentValidatedResidues, showLabels, createLabelForResidue]);

  // Simplified exosite highlighting for non-chain-visibility use cases
  const applyExositeHighlighting = useCallback((viewerInstance: any) => {
    if (!viewerInstance || currentValidatedResidues.length === 0) {
      return;
    }
    
    
    // This is now mainly for representation/color changes, not chain visibility
    // Chain visibility uses the complete rebuild approach
    currentValidatedResidues.forEach(residue => {
      if (!visibleChainsRef.current.has(residue.chain_id)) return;
      try {
        viewerInstance.addStyle(toMainModelSelection(residue), {
          stick: {
            color: '#dc2626',
            radius: 0.4
          }
        });
      } catch (e) {
        // Continue silently
      }
    });
  }, [currentValidatedResidues]);

  // Fetch PDB structure directly from RCSB
  const loadPdbStructure = useCallback(async (pdbIdToLoad: string) => {
    // Check if PDB ID contains commas (indicating it's not properly parsed)
    if (pdbIdToLoad.includes(',')) {
      setError(`Invalid PDB ID format: ${pdbIdToLoad}`);
      return;
    }
    
    if (!pdbIdToLoad || pdbIdToLoad.length < 3) {
      setError('Invalid PDB ID provided');
      return;
    }
    
    if (!viewerRef.current) {
      setError('3D viewer container not available');
      return;
    }
    
    if (!window.$3Dmol) {
      setError('3D viewer library not available');
      return;
    }

    activeLoadControllerRef.current?.abort();
    const controller = new AbortController();
    activeLoadControllerRef.current = controller;
    const loadGeneration = ++loadGenerationRef.current;
    const isCurrentLoad = () =>
      loadGenerationRef.current === loadGeneration && !controller.signal.aborted;

    setLoading(true);
    setError(null);

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (!isCurrentLoad()) return;
      setError(`Timeout loading PDB structure ${pdbIdToLoad.toUpperCase()}`);
      setLoading(false);
    }, 10000); // 10 second timeout

    try {
      // Clear existing viewer
      if (viewer) {
        viewer.clear();
      }

      // Create new viewer
      const newViewer = window.$3Dmol.createViewer(viewerRef.current, {
        defaultcolors: window.$3Dmol.elementColors.Jmol,
        backgroundColor: 'white'
      });
      
      setViewer(newViewer);

      // Try multiple PDB sources for better reliability
      const pdbSources = [
        `https://files.rcsb.org/download/${pdbIdToLoad.toLowerCase()}.pdb`,
        `https://www.rcsb.org/pdb/files/${pdbIdToLoad.toLowerCase()}.pdb`,
        `https://files.rcsb.org/view/${pdbIdToLoad.toLowerCase()}.pdb`
      ];
      
      let loadAttempt = 0;
      
      const tryLoadPdb = async () => {
        if (!isCurrentLoad()) return;
        if (loadAttempt >= pdbSources.length) {
          console.error(`❌ Exhausted all ${pdbSources.length} PDB sources`);
          setError(`Unable to load PDB structure ${pdbIdToLoad.toUpperCase()} from any source`);
          setLoading(false);
          clearTimeout(timeoutId);
          return;
        }
        
        const currentUrl = pdbSources[loadAttempt];
        loadAttempt++;
        
        try {
          // First, try to fetch the PDB data directly with CORS headers
          const response = await fetch(currentUrl, {
            method: 'GET',
            headers: {
              'Accept': 'text/plain,application/octet-stream,*/*'
            },
            mode: 'cors',
            signal: controller.signal,
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const pdbData = await response.text();

          if (!isCurrentLoad()) return;
          
          if (!pdbData || pdbData.length < 100) {
            throw new Error('Invalid or empty PDB data');
          }
          
          // Add the PDB data to the viewer
          newViewer.addModel(pdbData, 'pdb');
          
          clearTimeout(timeoutId); // Clear timeout on success
          
          try {
            // Detect available chains first
            const chains = detectAvailableChains(newViewer);
            
            // Set initial style after loading - chain-aware
            // Apply to all chains initially (all are visible by default)
            const initialChains = chains || ['A'];
            initialChains.forEach(chainId => {
              newViewer.setStyle({ model: 0, chain: chainId }, {
                [representationStyle]: {
                  colorscheme: colorScheme
                }
              });
            });

            // Highlight exosite residues and add labels if enabled
            applyExositeHighlighting(newViewer);
            
            // Add labels if enabled
            if (showLabels && currentValidatedResidues.length > 0) {
              const mainModel = newViewer.getModel(0);
              if (mainModel) {
                currentValidatedResidues.forEach(residue => {
                  try {
                    const chainAtoms = mainModel.selectedAtoms(toViewerSelection(residue));
                    if (chainAtoms && chainAtoms.length > 0) {
                      const label = createLabelForResidue(
                        chainAtoms,
                        residue.residue_number,
                        residue.chain_id
                      );
                      if (label) {
                        newViewer.addLabel(
                          `${residueDisplayLabel(residue)}:${residue.chain_id}`,
                          label.options
                        );
                      }
                    }
                  } catch (e) {
                  }
                });
              }
            }

            // Center and fit the structure
            newViewer.zoomTo();
            newViewer.render();
            
            // Set basic info for the structure
            setPdbInfo({
              success: true,
              pdb_id: pdbIdToLoad.toUpperCase(),
              title: `${pdbIdToLoad.toUpperCase()} from RCSB PDB`,
              message: 'Loaded successfully from RCSB PDB'
            });
            
            setLoading(false);
          } catch (renderError) {
            console.error('Error rendering structure:', renderError);
            setError('Failed to render 3D structure');
            setLoading(false);
          }
          
        } catch (fetchError) {
          if (!isCurrentLoad()) return;
          console.warn(`Failed to fetch from ${currentUrl}, trying 3Dmol.js direct loading...`, fetchError);
          
          // Fallback to 3Dmol.js built-in loading
          try {
            newViewer.addModel(currentUrl, 'pdb', {
              callback: () => {
                if (!isCurrentLoad()) return;
                clearTimeout(timeoutId);
                
                try {
                  // Detect available chains first
                  const chains = detectAvailableChains(newViewer);
                  
                  // Set initial style after loading - chain-aware
                  // Apply to all chains initially (all are visible by default)
                  const initialChains = chains || ['A'];
                  initialChains.forEach(chainId => {
                    newViewer.setStyle({ model: 0, chain: chainId }, {
                      [representationStyle]: {
                        colorscheme: colorScheme
                      }
                    });
                  });

                  // Highlight exosite residues and add labels if enabled (fallback)
                  applyExositeHighlighting(newViewer);
                  
                  // Add labels if enabled (fallback)
                  if (showLabels && currentValidatedResidues.length > 0) {
                    const mainModel = newViewer.getModel(0);
                    if (mainModel) {
                      currentValidatedResidues.forEach(residue => {
                        try {
                          const chainAtoms = mainModel.selectedAtoms(toViewerSelection(residue));
                          if (chainAtoms && chainAtoms.length > 0) {
                            const label = createLabelForResidue(
                              chainAtoms,
                              residue.residue_number,
                              residue.chain_id
                            );
                            if (label) {
                              newViewer.addLabel(
                                `${residueDisplayLabel(residue)}:${residue.chain_id}`,
                                label.options
                              );
                            }
                          }
                        } catch (e) {
                        }
                      });
                    }
                  }

                  newViewer.zoomTo();
                  newViewer.render();
                  
                  setPdbInfo({
                    success: true,
                    pdb_id: pdbIdToLoad.toUpperCase(),
                    title: `${pdbIdToLoad.toUpperCase()} from RCSB PDB`,
                    message: 'Loaded successfully from RCSB PDB'
                  });
                  
                  setLoading(false);
                } catch (renderError) {
                  console.error('Error rendering structure:', renderError);
                  setError('Failed to render 3D structure');
                  setLoading(false);
                }
              },
              onerror: () => {
                if (!isCurrentLoad()) return;
                // Try next source
                console.warn(`3Dmol.js also failed to load from ${currentUrl}, trying next source...`);
                tryLoadPdb();
              }
            });
          } catch (addModelError) {
            if (!isCurrentLoad()) return;
            console.error('Error with 3Dmol.js addModel:', addModelError);
            tryLoadPdb(); // Try next source
          }
        }
      };
      
      // Start loading with first source
      tryLoadPdb();

    } catch (err: any) {
      clearTimeout(timeoutId);
      if (!isCurrentLoad()) return;
      console.error('Error loading PDB structure:', err);
      setError(err.message || 'Failed to load structure');
      setLoading(false);
    }
  }, [
    representationStyle,
    colorScheme,
    showLabels,
    currentValidatedResidues,
    detectAvailableChains,
    applyExositeHighlighting,
    createLabelForResidue,
  ]);

  // Load structure when component mounts or PDB ID changes
  useEffect(() => {
    if (currentPdbId && window.$3Dmol) {
      loadPdbStructure(currentPdbId);
    } else if (currentPdbId && !window.$3Dmol) {
      // Wait for 3Dmol.js to load
      const checkInterval = setInterval(() => {
        if (window.$3Dmol) {
          clearInterval(checkInterval);
          loadPdbStructure(currentPdbId);
        }
      }, 100);
      
      // Clear interval after 5 seconds to prevent infinite checking
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.$3Dmol) {
          setError('Failed to load 3D viewer library');
        }
      }, 5000);
      
      return () => clearInterval(checkInterval);
    }
  }, [currentPdbId, loadPdbStructure]);

  // Update representation style with chain awareness
  const updateRepresentation = useCallback((style: string) => {
    if (viewer) {
      setRepresentationStyle(style);
      
      // Apply new representation style only to visible chains
      applyChainAwareStyles(viewer, visibleChains);

      // Re-highlight exosite residues only on visible chains
      applyExositeHighlighting(viewer);

      viewer.render();
    }
  }, [viewer, visibleChains, applyChainAwareStyles, applyExositeHighlighting]);

  // Update color scheme with chain awareness
  const updateColorScheme = useCallback((scheme: string) => {
    if (viewer) {
      setColorScheme(scheme);
      
      // Apply new color scheme only to visible chains
      applyChainAwareStyles(viewer, visibleChains);

      // Re-highlight exosite residues only on visible chains
      applyExositeHighlighting(viewer);

      viewer.render();
    }
  }, [viewer, visibleChains, applyChainAwareStyles, applyExositeHighlighting]);

  // Toggle labels on/off with chain awareness
  const toggleLabels = useCallback(() => {
    if (viewer) {
      const newShowLabels = !showLabels;
      setShowLabels(newShowLabels);
      
      
      // Use the complete rebuild approach to ensure labels are properly managed
      applyChainVisibility(viewer, availableChains, visibleChains, newShowLabels);
    }
  }, [viewer, showLabels, availableChains, visibleChains, applyChainVisibility]);

  // Take high-quality professional screenshot at 300px
  const takeScreenshot = useCallback(() => {
    if (!viewer) return;

    try {
      // Add visual feedback - flash effect
      const viewerElement = viewerRef.current;
      if (viewerElement) {
        viewerElement.style.transition = 'all 0.1s ease';
        viewerElement.style.filter = 'brightness(1.3)';
        setTimeout(() => {
          viewerElement.style.filter = 'brightness(1)';
        }, 100);
      }

      // Force a high-quality render at the current size
      viewer.render();
      
      // Wait a moment for the render to complete
      setTimeout(() => {
        try {
          // Get the current canvas
          const canvas = viewer.getCanvas();
          
          if (!canvas) {
            console.warn('Could not get canvas from viewer');
            return;
          }

          // Create high-resolution output canvas for final 300px image with supersampling
          const finalSize = 300;
          const supersampleSize = 1200; // 4x supersampling for better quality
          
          // Create supersampled canvas
          const supersampledCanvas = document.createElement('canvas');
          supersampledCanvas.width = supersampleSize;
          supersampledCanvas.height = supersampleSize;
          const supersampledCtx = supersampledCanvas.getContext('2d');

          if (!supersampledCtx) {
            console.warn('Could not get supersampled canvas context');
            return;
          }

          // Enable high-quality image smoothing for supersampling
          supersampledCtx.imageSmoothingEnabled = true;
          supersampledCtx.imageSmoothingQuality = 'high';

          // Set white background
          supersampledCtx.fillStyle = 'white';
          supersampledCtx.fillRect(0, 0, supersampleSize, supersampleSize);

          // Calculate proper scaling and centering for supersampling
          const sourceWidth = canvas.width;
          const sourceHeight = canvas.height;
          
          // Find the largest square that fits in the source
          const sourceSize = Math.min(sourceWidth, sourceHeight);
          const sourceX = (sourceWidth - sourceSize) / 2;
          const sourceY = (sourceHeight - sourceSize) / 2;

          // Draw to supersampled canvas (upscaling with high quality)
          supersampledCtx.drawImage(
            canvas,
            sourceX, sourceY, sourceSize, sourceSize,  // Source: center square
            0, 0, supersampleSize, supersampleSize  // Destination: full supersampled canvas
          );

          // Create final 300px output canvas
          const outputCanvas = document.createElement('canvas');
          outputCanvas.width = finalSize;
          outputCanvas.height = finalSize;
          const ctx = outputCanvas.getContext('2d');

          if (!ctx) {
            console.warn('Could not get output canvas context');
            return;
          }

          // Enable high-quality image smoothing for downsampling
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Set white background
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, finalSize, finalSize);

          // Downsample from supersampled canvas to final size (high-quality downsampling)
          ctx.drawImage(
            supersampledCanvas,
            0, 0, supersampleSize, supersampleSize,  // Source: full supersampled canvas
            0, 0, finalSize, finalSize  // Destination: final canvas
          );

          // Add subtle professional branding
          ctx.font = '10px system-ui, -apple-system, sans-serif';
          ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
          ctx.textAlign = 'right';
          ctx.textBaseline = 'bottom';
          ctx.fillText('STINGExoDB', finalSize - 8, finalSize - 4);

          // Generate descriptive filename
          const proteinInfo = pdbInfo?.pdb_id || currentPdbId || 'protein';
          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          const filename = `${proteinInfo}_structure_${timestamp}_300px.png`;

          // Export as high-quality PNG
          outputCanvas.toBlob((blob) => {
            if (!blob) {
              console.warn('Could not create image blob');
              return;
            }
            
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up
            URL.revokeObjectURL(url);
            
          }, 'image/png', 1.0); // Maximum quality

        } catch (renderError) {
          console.error('Error in screenshot render:', renderError);
        }
      }, 200); // Give time for render to complete

    } catch (error) {
      console.error('Error taking screenshot:', error);
    }
  }, [viewer, pdbInfo, currentPdbId]);

  // Load binding partner structure
  const loadBindingPartnerStructure = useCallback(async (partnerData: any) => {
    if (!viewer || !partnerData.structure_available || !partnerData.recommended_pdb) {
      return null;
    }

    const pdbId = partnerData.recommended_pdb;
    
    // Check if already loaded
    if (loadedPartnerModels.has(pdbId)) {
      return loadedPartnerModels.get(pdbId);
    }

    try {
      
      // Try to fetch PDB structure from multiple sources
      let pdbData = null;
      let pdbSource = '';
      
      // First try our API endpoint
      try {
        const response = await fetch(`/api/pdb/${pdbId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.pdb_data) {
            pdbData = data.pdb_data;
            pdbSource = 'API';
          }
        }
      } catch (e) {
      }
      
      // Use backend proxy to fetch PDB file (avoids CORS issues)
      if (!pdbData) {
        try {
          const backendUrl = '';
          const proxy_url = `${backendUrl}/pdb/${pdbId}/file`;
          
          const response = await fetch(proxy_url, {
            headers: {
              'Accept': 'text/plain,*/*'
            }
          });
          
          if (response.ok) {
            pdbData = await response.text();
            pdbSource = 'Backend Proxy';
          } else {
          }
        } catch (e) {
        }
      }
      
      if (!pdbData) {
        throw new Error(`Could not fetch PDB data for ${pdbId} from any source`);
      }

      
      // Add partner model to viewer
      const partnerModel = viewer.addModel(pdbData, 'pdb');
      
      // Position the partner structure near exosite (if available)
      const visibleValidatedResidues = currentValidatedResidues.filter(
        residue => visibleChains.has(residue.chain_id)
      );
      if (visibleValidatedResidues.length > 0) {
        // Find the center of mass of exosite residues
        let exositeCenterX = 0, exositeCenterY = 0, exositeCenterZ = 0;
        let exositeAtomCount = 0;
        
        visibleValidatedResidues.forEach(residue => {
          try {
            const exositeAtoms = viewer.getModel(0).selectedAtoms(toViewerSelection(residue));
            if (exositeAtoms.length > 0) {
              exositeAtoms.forEach((atom: any) => {
                exositeCenterX += atom.x;
                exositeCenterY += atom.y;
                exositeCenterZ += atom.z;
                exositeAtomCount++;
              });
            }
          } catch (e) {
            console.warn(`Could not get atoms for residue ${residueDisplayLabel(residue)}`);
          }
        });
        
        if (exositeAtomCount > 0) {
          // Calculate average position of exosite residues
          exositeCenterX /= exositeAtomCount;
          exositeCenterY /= exositeAtomCount;
          exositeCenterZ /= exositeAtomCount;
          
          // Position partner structure ~20Å away from exosite center
          const offsetDistance = 20;
          partnerModel.translate({ 
            x: exositeCenterX + offsetDistance, 
            y: exositeCenterY, 
            z: exositeCenterZ + 10 
          });
          
        } else {
          // Fallback: position relative to main protein
          const mainProteinAtoms = viewer.getModel(0).selectedAtoms({});
          if (mainProteinAtoms.length > 0) {
            partnerModel.translate({ x: 50, y: 0, z: 0 });
          }
        }
      } else {
        // No exosite info: position at standard offset
        partnerModel.translate({ x: 50, y: 0, z: 0 });
      }
      
      // Style the partner structure distinctly
      viewer.setStyle({ model: partnerModel }, {
        cartoon: {
          color: partnerData.color,
          opacity: 0.8
        }
      });
      
      // Store loaded model
      const newLoadedModels = new Map(loadedPartnerModels);
      newLoadedModels.set(pdbId, partnerModel);
      setLoadedPartnerModels(newLoadedModels);
      
      return partnerModel;
      
    } catch (error) {
      console.error(`❌ Error loading partner structure ${pdbId}:`, error);
      return null;
    }
  }, [viewer, loadedPartnerModels, currentValidatedResidues, visibleChains]);

  // Apply binding partner visual emphasis in 3D structure
  const applyBindingPartnerVisualization = useCallback(async (viewerInstance: any, partnerName: string | null) => {
    if (!viewerInstance) return;
    
    try {
      if (!partnerName) {
        // Reset to normal visualization when no partner is selected
        
        // Hide all partner structures if not in "show all" mode
        if (!showPartnerStructures) {
          loadedPartnerModels.forEach((model, pdbId) => {
            viewerInstance.setStyle({ model: model }, {});
          });
        }
        
        // Reset protein to normal representation - apply to visible chains only
        visibleChains.forEach(chainId => {
          viewerInstance.setStyle({ model: 0, chain: chainId }, {
            [representationStyle]: {
              colorscheme: colorScheme
            }
          });
        });
        
        // Re-apply normal exosite highlighting
        applyExositeHighlighting(viewerInstance);
        
      } else {
        // Apply visual emphasis when partner is selected
        
        // Find the partner data
        const selectedPartner = bindingPartnersList.find(p => p.name === partnerName);
        
        if (selectedPartner) {
          // Load partner structure if available and not already loaded
          let partnerModel = null;
          if (selectedPartner.structure_available) {
            partnerModel = await loadBindingPartnerStructure(selectedPartner);
            
            // Show the partner structure
            if (partnerModel) {
              viewerInstance.setStyle({ model: partnerModel }, {
                cartoon: {
                  color: selectedPartner.color,
                  opacity: 0.8
                }
              });
            }
          }
          
          // Subtle protein background adjustment to focus attention - only visible chains
          visibleChains.forEach(chainId => {
            viewerInstance.setStyle({ model: 0, chain: chainId }, {
              [representationStyle]: {
                colorscheme: colorScheme,
                opacity: 0.7  // Slightly transparent to emphasize interactions
              }
            });
          });
          
          // Enhanced exosite highlighting since partners often interact with exosites
          const visibleValidatedResidues = currentValidatedResidues.filter(
            residue => visibleChains.has(residue.chain_id)
          );
          if (visibleValidatedResidues.length > 0) {
            visibleValidatedResidues.forEach(residue => {
              // Enhanced highlighting for partner interaction context
              viewerInstance.setStyle(
                toMainModelSelection(residue),
                {
                  stick: {
                    colorscheme: 'red',
                    radius: 0.4
                  }
                }
              );

              // Add a subtle glow effect around interaction sites
              viewerInstance.setStyle(
                { ...toMainModelSelection(residue), atom: 'CA' },
                {
                  sphere: {
                    color: selectedPartner.color,
                    radius: 2.5,
                    opacity: 0.3
                  }
                }
              );
            });
          }
          
          // Add information label for the selected partner
          if (showLabels && visibleValidatedResidues.length > 0) {
            // Add a contextual label near the first exosite residue
            const firstResidue = visibleValidatedResidues[0];
            const atoms = viewerInstance.selectedAtoms({
              ...toMainModelSelection(firstResidue),
              atom: 'CA'
            });
            if (atoms.length > 0) {
              const caAtom = atoms[0];
              const labelText = partnerModel ? 
                `Partner: ${selectedPartner.name}\nType: ${selectedPartner.type}\nStructure: ${selectedPartner.recommended_pdb}` :
                `Partner: ${selectedPartner.name}\nType: ${selectedPartner.type}\nNo 3D structure`;
              
              viewerInstance.addLabel(
                labelText,
                {
                  position: { x: caAtom.x + 8, y: caAtom.y + 8, z: caAtom.z + 8 },
                  backgroundColor: selectedPartner.color,
                  backgroundOpacity: 0.9,
                  fontColor: 'white',
                  fontSize: 12,
                  fontOpacity: 1.0,
                  alignment: 'center'
                }
              );
            }
          }
        }
      }
      
      viewerInstance.render();
    } catch (e) {
      console.error('❌ Error applying binding partner visualization:', e);
      viewerInstance.render(); // Force render even if there was an error
    }
  }, [representationStyle, colorScheme, applyExositeHighlighting,
      currentValidatedResidues, visibleChains, bindingPartnersList, showLabels, loadBindingPartnerStructure,
      loadedPartnerModels, showPartnerStructures]);

  // Handle binding partner selection changes
  useEffect(() => {
    if (viewer) {
      applyBindingPartnerVisualization(viewer, selectedBindingPartner);
    }
  }, [selectedBindingPartner, viewer, applyBindingPartnerVisualization]);

  // Toggle chain visibility
  const toggleChainVisibility = useCallback((chainId: string) => {
    const newVisibleChains = new Set(visibleChains);
    if (newVisibleChains.has(chainId)) {
      newVisibleChains.delete(chainId);
    } else {
      newVisibleChains.add(chainId);
    }
    
    visibleChainsRef.current = newVisibleChains;
    setVisibleChains(newVisibleChains);
    
    if (viewer) {
      applyChainVisibility(viewer, availableChains, newVisibleChains);
      
      // Re-apply both exosite highlighting and binding partner visualization after chain visibility changes
      applyExositeHighlighting(viewer);
      applyBindingPartnerVisualization(viewer, selectedBindingPartner);
    } else {
      console.warn('⚠️ No viewer available for chain toggle');
    }
  }, [visibleChains, viewer, availableChains,
      selectedBindingPartner, applyChainVisibility, applyExositeHighlighting,
      applyBindingPartnerVisualization]);

  // Show/hide all chains
  const toggleAllChains = useCallback((visible: boolean) => {
    const newVisibleChains = visible ? new Set(availableChains) : new Set<string>();
    visibleChainsRef.current = newVisibleChains;
    setVisibleChains(newVisibleChains);
    
    if (viewer) {
      applyChainVisibility(viewer, availableChains, newVisibleChains);
      
      // Re-apply both exosite highlighting and binding partner visualization after chain visibility changes
      applyExositeHighlighting(viewer);
      applyBindingPartnerVisualization(viewer, selectedBindingPartner);
    }
  }, [availableChains, viewer, selectedBindingPartner,
      applyChainVisibility, applyExositeHighlighting, applyBindingPartnerVisualization]);

  // Fetch binding partner structural data from API
  const fetchBindingPartnerStructures = useCallback(async () => {
    if (!entryId) {
      const parsedPartners = parseBindingPartners(bindingPartners);
      setBindingPartnersList(parsedPartners);
      return;
    }

    try {
      const backendUrl = '';
      const response = await fetch(`${backendUrl}/entries/${entryId}/binding-partners-structures`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      setPartnerStructures(data.binding_partners);
      // Also update the display list for backward compatibility
      const displayPartners = data.binding_partners.map((p: BindingPartnerStructure, index: number) => ({
        id: `partner_${index}`,
        name: p.name,
        type: p.type,
        description: p.description,
        color: p.color,
        originalIndex: index,
        structure_available: p.structure_available,
        pdb_candidates: p.pdb_candidates,
        recommended_pdb: p.recommended_pdb
      }));
      setBindingPartnersList(displayPartners);
      
    } catch (error) {
      console.error('❌ Error fetching binding partner structures:', error);
      // Fallback to local parsing
      const parsedPartners = parseBindingPartners(bindingPartners);
      setBindingPartnersList(parsedPartners);
    }
  }, [entryId, bindingPartners, parseBindingPartners]);

  // Parse binding partners when they change
  useEffect(() => {
    fetchBindingPartnerStructures();
  }, [fetchBindingPartnerStructures]);


  const handlePdbChange = (newPdbId: string) => {
    setSelectedPdbId(newPdbId);
    setShowPdbSelector(false);
    if (onPdbChange) {
      onPdbChange(newPdbId);
    }
  };

  if (!currentPdbId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '32px' }}>
        <InformationCircleIcon style={{ 
          height: '48px', 
          width: '48px', 
          color: '#9ca3af', 
          margin: '0 auto 16px' 
        }} />
        <p style={{ color: '#64748b' }}>No PDB structure available</p>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        padding: '16px', 
        borderBottom: '1px solid #e2e8f0', 
        backgroundColor: '#f8fafc' 
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1e293b',
              margin: 0
            }}>
              3D Structure:
            </h3>
            
            {availablePdbIds.length > 1 ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowPdbSelector(!showPdbSelector)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 12px',
                    backgroundColor: 'white',
                    border: '2px solid #3b82f6',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    color: '#1e293b',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{currentPdbId.toUpperCase()}</span>
                  {currentPdbId === representativePdbId && (
                    <StarIcon style={{ height: '14px', width: '14px', color: '#f59e0b' }} />
                  )}
                  <ChevronDownIcon style={{ height: '16px', width: '16px', color: '#64748b' }} />
                </button>
                
                {showPdbSelector && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 10,
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    minWidth: '200px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}>
                    {availablePdbIds.map((pdbIdOption) => (
                      <button
                        key={pdbIdOption}
                        onClick={() => handlePdbChange(pdbIdOption)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          textAlign: 'left',
                          backgroundColor: pdbIdOption === currentPdbId ? '#eff6ff' : 'transparent',
                          color: pdbIdOption === currentPdbId ? '#1d4ed8' : '#374151',
                          border: 'none',
                          borderBottom: '1px solid #f1f5f9',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontFamily: 'monospace',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (pdbIdOption !== currentPdbId) {
                            e.currentTarget.style.backgroundColor = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (pdbIdOption !== currentPdbId) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <span style={{ fontWeight: '600' }}>{pdbIdOption.toUpperCase()}</span>
                        {pdbIdOption === representativePdbId && (
                          <StarIcon style={{ height: '12px', width: '12px', color: '#f59e0b' }} />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <span style={{
                fontSize: '18px',
                fontWeight: '600',
                fontFamily: 'monospace',
                color: '#3b82f6'
              }}>
                {currentPdbId.toUpperCase()}
              </span>
            )}

            {/* Reload button */}
            <button
              onClick={() => loadPdbStructure(currentPdbId)}
              disabled={loading}
              style={{
                padding: '6px',
                backgroundColor: loading ? '#e2e8f0' : '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              title="Reload structure"
            >
              <RefreshIcon style={{ 
                height: '16px', 
                width: '16px', 
                color: loading ? '#9ca3af' : '#64748b',
                transform: loading ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.5s'
              }} />
            </button>
          </div>
          
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {currentValidatedResidues.length > 0 ? (
              <div style={{ marginBottom: '8px' }}>
                <span style={{ fontWeight: '500' }}>Verified PDB residues:</span>
                <div style={{ 
                  marginTop: '4px',
                  padding: '8px',
                  backgroundColor: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '4px',
                  fontSize: '13px',
                  lineHeight: '1.4',
                  wordBreak: 'break-word',
                  maxHeight: '100px',
                  overflowY: 'auto'
                }}>
                  <strong style={{ color: '#047857' }}>
                    {currentValidatedResidues.map(residue =>
                      `${residue.chain_id}:${residueDisplayLabel(residue)}`
                    ).join(', ')}
                  </strong>
                </div>
              </div>
            ) : mappingStatus ? (
              <div style={{
                marginBottom: '8px',
                padding: '8px',
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '4px',
                color: '#92400e',
                fontSize: '13px',
                lineHeight: '1.4'
              }}>
                <strong>Residue highlighting withheld.</strong>{' '}
                {mappingStatus === 'verified'
                  ? 'No verified coordinates are available for the structure currently selected.'
                  : mappingValidationReason || 'This legacy mapping has not passed structural validation.'}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              {pdbInfo?.success && pdbInfo.experimental_method && (
                <span>
                  Method: {pdbInfo.experimental_method}
                </span>
              )}
              {pdbInfo?.success && pdbInfo.resolution && pdbInfo.resolution.length > 0 && (
                <span>
                  Resolution: {pdbInfo.resolution[0]}Å
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {onClose && (
            <button
              onClick={onClose}
              className="btn-secondary"
              style={{ 
                padding: '8px', 
                color: '#dc2626',
                backgroundColor: '#fef2f2'
              }}
              title={isFullscreen ? "Exit fullscreen" : "Close viewer"}
            >
              <XMarkIcon style={{ height: '16px', width: '16px' }} />
            </button>
          )}
        </div>
      </div>

      {/* Controls */}
      <div style={{ 
        padding: '12px 16px',
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            Style:
          </label>
          <select
            value={representationStyle}
            onChange={(e) => updateRepresentation(e.target.value)}
            style={{
              padding: '4px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white'
            }}
          >
            <option value="cartoon">Cartoon</option>
            <option value="stick">Ball & Stick</option>
            <option value="line">Line</option>
            <option value="sphere">Sphere</option>
          </select>
        </div>

        {/* Labels are available only for coordinates that passed validation. */}
        {currentValidatedResidues.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showLabels}
                onChange={toggleLabels}
                style={{ 
                  width: '16px', 
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Show Labels
              </span>
            </label>
          </div>
        )}

        {/* Professional Screenshot Button */}
        <div>
          <button
            onClick={takeScreenshot}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#3b82f6';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
            }}
            disabled={!viewer || loading}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zm16 2H4v12h16V6zm-3 3.5a.5.5 0 1 0-1 0 .5.5 0 0 0 1 0zM8.5 14L11 10.5 13.5 14 8.5 14z"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
            </svg>
            <span>Screenshot</span>
          </button>
        </div>

        {/* Professional Chain Information - responsive layout */}
        {availableChains.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Chains ({availableChains.length}):
            </label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              padding: '8px 12px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '12px',
              maxWidth: '100%',
              minHeight: '32px'
            }}>
              {availableChains.slice(0, 12).map(chainId => {
                const info = chainInfo.get(chainId);
                return (
                  <div
                    key={chainId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      padding: '2px 6px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      border: '1px solid #e5e7eb',
                      minWidth: 'fit-content',
                      maxWidth: '80px'
                    }}
                    title={`Chain ${chainId}${info?.type ? ` (${info.type})` : ''}${info?.description ? ` - ${info.description}` : ''}${info?.exositeCount ? ` - ${info.exositeCount} exosite residue${info.exositeCount > 1 ? 's' : ''}` : ''}`}
                  >
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '2px',
                        backgroundColor: getChainColor(chainId),
                        flexShrink: 0
                      }}
                    />
                    <span style={{ 
                      fontFamily: 'monospace',
                      fontWeight: '600', 
                      fontSize: '11px', 
                      color: '#374151',
                      whiteSpace: 'nowrap'
                    }}>
                      {chainId}
                    </span>
                    {info?.exositeCount > 0 && (
                      <span style={{
                        fontSize: '9px',
                        color: '#dc2626',
                        backgroundColor: '#fef2f2',
                        padding: '1px 3px',
                        borderRadius: '2px',
                        border: '1px solid #fecaca',
                        flexShrink: 0
                      }}>
                        {info.exositeCount}
                      </span>
                    )}
                  </div>
                );
              })}
              
              {availableChains.length > 12 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    padding: '2px 8px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db'
                  }}
                  title={`${availableChains.length - 12} additional chains: ${availableChains.slice(12).join(', ')}`}
                >
                  <span style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>
                    +{availableChains.length - 12}
                  </span>
                </div>
              )}
              
              {/* Summary info */}
              <div style={{ 
                marginLeft: 'auto', 
                fontSize: '10px', 
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 4px',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '3px',
                whiteSpace: 'nowrap'
              }}>
                <span>Total: {availableChains.length}</span>
                {availableChains.some(chainId => chainInfo.get(chainId)?.exositeCount > 0) && (
                  <span style={{ color: '#dc2626', fontWeight: '500' }}>
                    • {availableChains.reduce((sum, chainId) => sum + (chainInfo.get(chainId)?.exositeCount || 0), 0)} exosite residues
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* 3D Viewer */}
      <div style={{ position: 'relative' }}>
        {error && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #fecaca',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Error loading structure</p>
            <p style={{ margin: 0, fontSize: '14px' }}>{error}</p>
          </div>
        )}
        
        {loading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            backgroundColor: 'rgba(248, 250, 252, 0.9)',
            padding: '24px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              Loading structure...
            </p>
          </div>
        )}

        <div style={{ position: 'relative' }}>
          <div
            ref={viewerRef}
            style={{ 
              height: '500px',
              width: '100%',
              backgroundColor: '#f8fafc',
              border: 'none',
              position: 'relative'
            }}
          />
          
          {/* Floating Expand Button - Positioned over canvas */}
          {onExpand && !isFullscreen && (
            <button
              onClick={onExpand}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '32px',
                height: '32px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title="Expand to fullscreen"
            >
              <ExpandIcon style={{ 
                height: '16px', 
                width: '16px', 
                color: '#374151',
                strokeWidth: 2.5
              }} />
            </button>
          )}
        </div>
      </div>

      {/* Footer with structure info */}
      {pdbInfo?.success && (
        <div style={{ 
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          fontSize: '14px',
          color: '#64748b'
        }}>
          {pdbInfo.title && (
            <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>
              {pdbInfo.title}
            </p>
          )}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {pdbInfo.release_date && (
              <span>Released: {pdbInfo.release_date}</span>
            )}
            <a
              href={`https://www.rcsb.org/structure/${currentPdbId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#3b82f6', textDecoration: 'none' }}
            >
              View on RCSB PDB ↗
            </a>
          </div>
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProteinViewer;
